import React, { useEffect, useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import { editObject, getObject, addObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import Form from '../../form/Form'
import { getCustomerFields } from './CustomerFields'
import useSaveEntity from '../../hooks/useSaveEntity'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import Toolbar from '../../toolbar/Toolbar'

const ROUTES = {
  EDIT: '/customers/edit',
  VIEW: '/customers/view',
  ADD: '/customers/add'
}

const buildPayload = ({ data, user, mode }) => {
  const isEdit = mode === 'edit'

  const sanitizedData = {
    ...data,
    ...(data?.customer_type === 2 ? { siret: null, siren: null } : {})
  }

  return {
    ...sanitizedData,
    ...(isEdit
      ? { lastModBy: user.email }
      : {
          owner_id: user.company_id,
          company_id: user.company_id,
          createdBy: user.email
        })
  }
}

const EditCustomer = () => {
  const [customer, setCustomer] = useState({})
  const [isSubmit, setIsSubmit] = useState(false)
  const history = useNavigate()
  const { user } = useAuth()
  const location = useLocation()

  const path = location.pathname
  const customerId = location.state?.id

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  const customerQuery = mode === 'edit' ? { key: 'customer', fn: getObject, args: ['customers', customerId] } : null

  const queries = [customerQuery].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'customers',
    mode,
    id: customerId,
    user,
    addFn: addObject,
    editFn: editObject,
    updateCounter: false,
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: () => setIsSubmit(false),
    onError: () => setIsSubmit(false),
    buildPayload
  })

  const memoizedFields = useMemo(() => getCustomerFields(customer), [customer])

  const options = useMemo(() => ({ fields: memoizedFields, data: customer }), [memoizedFields, customer])

  const toolbarOptions = useMemo(
    () => ({
      title: () => {
        return path.includes('/add') ? 'Nouveau Client' : `Client ${customer.customer_name}`
      }
    }),
    [customer, path]
  )

  useEffect(() => {
    if (!loadingEntities) {
      if (fetched.customer) setCustomer(fetched.customer)
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    if (isSubmit) {
      saveEntity({ data: customer })
    }
  }, [isSubmit])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1
      }}
    >
      <Toolbar options={toolbarOptions} object={customer} />

      <Form
        mode={mode}
        options={options}
        setDataParent={setCustomer}
        setIsSubmitParent={setIsSubmit}
        loading={loading}
      />
    </Box>
  )
}

export default EditCustomer
